import * as Sentry from '@sentry/react';
import { LoaderFunctionArgs, createBrowserRouter } from 'react-router-dom';

import { loadContext, loaders } from 'src/api/loaders';
import { AdminContainer } from 'src/routes/admin/container';
import { AdminCreateSPOptimizerExperiment } from 'src/routes/admin/sp-optimizer-experiment';
import { DashboardContainer } from 'src/routes/dashboard/container';
import { DashboardHome } from 'src/routes/dashboard/home';
import { DashboardIntegrations } from 'src/routes/dashboard/integrations';
import { DashboardStore } from 'src/routes/dashboard/store';
import { DashboardVideoFlow } from 'src/routes/dashboard/video-flow';
import { DashboardVideoFlowGenerateScript } from 'src/routes/dashboard/video-flow-generate-script';
import { DashboardVideoFlowList } from 'src/routes/dashboard/video-flow-list';
import { Videos } from 'src/routes/video-landing-page/videos';
import { VideosEmbed } from 'src/routes/video-landing-page/videos-embed';

import type { QueryClient } from '@tanstack/react-query';

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouter(createBrowserRouter);

export const router = (queryClient: QueryClient) =>
  sentryCreateBrowserRouter([
    {
      path: '/',
      id: 'root',
      element: <></>,
    },
    {
      path: '/videos/:uuid',
      id: 'video',
      element: <Videos />,
      loader: () => loadContext(),
    },
    {
      path: '/videos-embed/:uuid',
      id: 'video-embed',
      element: <VideosEmbed />,
      loader: () => loadContext(),
    },
    {
      path: '/app',
      id: 'app',
      element: <DashboardContainer />,
      loader: () => ({ ...loadContext(), usecases: [] }),
      children: [{ index: true, element: <DashboardStore /> }],
    },
    {
      path: '/app/:storeKey',
      id: 'app-container',
      element: <DashboardContainer />,
      loader: async (args: LoaderFunctionArgs) => {
        const usecasesLoader = loaders.usecasesForStoreLoader(queryClient);
        const usecases = await usecasesLoader(args);
        return { ...loadContext(), usecases };
      },
      children: [
        { index: true, id: 'home', element: <DashboardHome /> },
        {
          path: 'video-flows',
          id: 'video-flows',
          loader: loaders.usecasesForStoreLoader(queryClient),
          element: <DashboardVideoFlowList />,
        },
        {
          path: 'video-flows/:usecaseId/generate-script',
          id: 'video-flows-usecase-generate-script',
          loader: loaders.usecaseLoader(queryClient),
          element: <DashboardVideoFlowGenerateScript />,
        },
        {
          path: 'video-flows/:usecaseId',
          id: 'video-flows-usecase',
          loader: loaders.usecaseLoader(queryClient),
          element: <DashboardVideoFlow />,
        },
        { path: 'integrations/', id: 'integrations', element: <DashboardIntegrations /> },
        { path: 'store/', id: 'store', element: <DashboardStore /> },
      ],
    },
    {
      path: '/admin',
      id: 'admin',
      loader: () => loadContext(),
      element: <AdminContainer />,
      children: [
        {
          path: 'create-sp-optimizer-experiment/:flowMessage',
          id: 'create-sp-optimizer-experiment',
          element: <AdminCreateSPOptimizerExperiment />,
        },
      ],
    },
  ]);
